import { Image } from "@nextui-org/react";

import { Col, Row, Text } from "@/components/customComponents";
import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import { APPLICATIONS_ID } from "@/constants";
import { useMediaStore } from "@/hooks";
import { cn } from "@/utils";

import { type ApplicationCardDetails, type ApplicationDownloadLink } from "./utils";

type ApplicationCardProps = {
  appCardData: ApplicationCardDetails;
};

const NON_DESKTOP_IMAGE_HEIGHT = 300;
const MOBILE_IMAGE_WIDTH = 278;
const TABLET_IMAGE_WIDTH = 237;

const getBackgroundStyles = (backgroundColor: string): React.CSSProperties => ({
  background: backgroundColor,
  boxShadow: `0px 0px 200px 200px ${backgroundColor}`,
});

const getImageWidth = ({ isMobile, isTablet }: { isMobile: boolean; isTablet: boolean }) => {
  if (isMobile) {
    return MOBILE_IMAGE_WIDTH;
  }

  if (isTablet) {
    return TABLET_IMAGE_WIDTH;
  }

  return undefined;
};

function ApplicationCardLogo({ isSpicyChat, logo }: { isSpicyChat: boolean; logo?: string }) {
  if (!logo) {
    return null;
  }

  return <Image removeWrapper height={48} width={48} className={cn("p-1 bg-black rounded-xl", isSpicyChat && "p-2")} src={logo} />;
}

function ApplicationCardDownloadLinks({ downloadLinks, isMobile }: { downloadLinks: ApplicationDownloadLink[]; isMobile: boolean }) {
  if (!downloadLinks.length) {
    return null;
  }

  return (
    <Row className={"gap-1 mt-auto " + (isMobile ? "justify-center" : undefined)}>
      {downloadLinks.map((downloadLink) => (
        <LocalizedLink
          key={downloadLink.platform}
          to={downloadLink.redirectLink}
          target={downloadLink.target}
          aria-label={downloadLink.platform}
          data-testid={`ApplicationCard-${downloadLink.platform}Link`}
        >
          <Image removeWrapper radius="none" height={38} src={downloadLink.badgeImage} />
        </LocalizedLink>
      ))}
    </Row>
  );
}

function ApplicationCardPhoneImage({
  backgroundImage,
  imageWidth,
  isDesktop,
  isMobile,
}: {
  backgroundImage?: string;
  imageWidth: number | undefined;
  isDesktop: boolean;
  isMobile: boolean;
}) {
  if (!backgroundImage) {
    return null;
  }

  return (
    <div
      className={
        !isDesktop ? `h-[280px] min-w-[237px] overflow-hidden relative top-6 ${!isMobile ? "self-end" : "self-center"}` : "flex items-center"
      }
    >
      <Image
        removeWrapper
        src={backgroundImage}
        width={imageWidth}
        height={!isDesktop ? undefined : NON_DESKTOP_IMAGE_HEIGHT}
        className="rounded-xl border-solid border-[#8F8F8F] !shadow-[0px_4px_42.3px_0px_rgba(250,250,250,0.25)]"
      />
    </div>
  );
}

export default function ApplicationCard({ appCardData }: ApplicationCardProps) {
  const isSpicyChat = appCardData.id === APPLICATIONS_ID.SpicyChat;
  const isMobile = useMediaStore((state) => state.isMobile);
  const isTablet = useMediaStore((state) => state.isTablet);
  const isDesktop = useMediaStore((state) => state.isDesktop);
  const imageWidth = getImageWidth({ isMobile, isTablet });

  return (
    <Row
      data-testid="ApplicationCard"
      className={
        "rounded-2xl border-1 border-solid border-neutral-borderSecondary relative *:z-[1] overflow-hidden min-h-[380px] max-desktop:min-h-[350px] mob:gap-[22px] mob:p-6 max-mob:flex-col max-mob:text-center max-mob:p-2.5 w-[628px] max-w-full max-desktop:w-full"
      }
    >
      <div
        className="absolute size-[124px] top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full opacity-0 dark:opacity-80 z-[0]"
        style={getBackgroundStyles(appCardData.backgroundColor)}
      />
      <Col className={"gap-4 " + (isMobile && "items-center")}>
        <Col className={"gap-2 " + (isMobile && "!flex-row items-center justify-center")}>
          <ApplicationCardLogo isSpicyChat={isSpicyChat} logo={appCardData.logo} />
          <Text b type="neutral-text">
            {appCardData.applicationName}
          </Text>
        </Col>
        <Text b variant="lg" type="neutral-text">
          {appCardData.title}
        </Text>
        <Text type="neutral-text">{appCardData.description}</Text>

        <ul className={"*:list-disc pl-6 text-start " + (isMobile ? "mx-10" : "")}>
          {appCardData.features.map((feature, index) => (
            <li key={index}>{feature}</li>
          ))}
        </ul>

        {appCardData.note ? (
          <Text variant="smallest" type="neutral_secondary tracking-wide">
            {appCardData.note}
          </Text>
        ) : null}

        <ApplicationCardDownloadLinks downloadLinks={appCardData.downloadLinks} isMobile={isMobile} />
      </Col>
      <ApplicationCardPhoneImage backgroundImage={appCardData.backgroundImage} imageWidth={imageWidth} isDesktop={isDesktop} isMobile={isMobile} />
    </Row>
  );
}
